import { useCallback } from 'react';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { isEmpty } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { ComponentLoader } from 'components';
import PaginationRow from 'components/atoms/GefMui/PaginationRow';
import SectionHeader from 'components/atoms/GefMui/SectionHeader';
import TableHasNoResults from 'components/atoms/GefMui/TableHasNoResults';
import fetch from 'features/common/fetch';
import guarantorsEdit from 'features/views/guarantorsEdit';
import { equalOrThreeDashes } from 'utils/equalOrThreeDashes';
import { joinString } from 'utils/string';

export const GuarantorsTable = () => {
	const dispatch = useDispatch();
	const guarantors = useSelector(guarantorsEdit.selectors.getGuarantors);
	const selectedGuarantors = useSelector(guarantorsEdit.selectors.getSelectedGuarantors);
	const fetchState = useSelector(fetch.selectors.getFetchState(fetch.constants.urls.campaignGuarantorCandidate));

	const handleReload = useCallback(() => {
		dispatch(guarantorsEdit.actions.reload());
	}, [dispatch]);

	const handleLoadMore = useCallback(() => {
		dispatch(guarantorsEdit.actions.loadMore());
	}, [dispatch]);

	const handlePaginationClick = useCallback(
		(e, page) => {
			dispatch(guarantorsEdit.actions.loadPage(page - 1));
		},
		[dispatch],
	);

	const handleSelect = useCallback(
		(selectedId) => () => {
			let selected;
			if (selectedGuarantors.includes(selectedId)) {
				selected = selectedGuarantors.filter((id) => id !== selectedId);
			} else {
				selected = selectedGuarantors.concat(selectedId);
			}
			dispatch(guarantorsEdit.actions.selectGuarantors(selected));
		},
		[dispatch, selectedGuarantors],
	);

	const hasRecords = !isEmpty(guarantors);

	return (
		<Stack spacing={2}>
			<SectionHeader text={getLabelMessage('guarantors:result')} />
			<ComponentLoader fetchStorePath={fetch.constants.urls.campaignGuarantorCandidate} onReload={handleReload}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>{getLabelMessage('guarantors:nameAndSurname')}</TableCell>
							<TableCell>{getLabelMessage('guarantors:sellerIn')}</TableCell>
							<TableCell>{getLabelMessage('guarantors:login')}</TableCell>
							<TableCell>{getLabelMessage('guarantors:profession')}</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{!hasRecords ? (
							<TableHasNoResults fetchState={fetchState} />
						) : (
							guarantors.map((guarantor) => (
								<TableRow
									key={guarantor.id}
									onClick={handleSelect(guarantor.id)}
									hover={!selectedGuarantors.includes(guarantor.id)}
									sx={{
										backgroundColor: selectedGuarantors.includes(guarantor.id)
											? 'primary.shades.8p'
											: 'default',
									}}
								>
									<TableCell>{joinString(' ', [guarantor.surname, guarantor.firstName])}</TableCell>
									<TableCell>{equalOrThreeDashes(guarantor.sellerIn)}</TableCell>
									<TableCell>{guarantor.login}</TableCell>
									<TableCell>{equalOrThreeDashes(guarantor.profession)}</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
					<PaginationRow
						fetchStorePath={fetch.constants.urls.campaignGuarantorCandidate}
						onLoadNext={handleLoadMore}
						onPaginationClick={handlePaginationClick}
					/>
				</Table>
			</ComponentLoader>
		</Stack>
	);
};

export default GuarantorsTable;
